import { useMemo } from 'react';

export const useModifiers = (
  styles: Record<string, string>,
  baseClassName: string,
  /** List of baseClassName modifiers, which can be either static or dynamic.
   *
   * Static:
   *
   * 1) Each modifier should have a key and a boolean condition.
   * Example: { white: isBetsOpen } => Result: <baseClassName>_white will be added if isBetsOpen is true.
   * 2) If you need to add a modifier permanently, you can use the boolean value `true` as the condition.
   * Example: { red: true } => Result: <baseClassName>_red will always be added.
   *
   * Dynamic:
   *
   * Additionally, modifiers support dynamic classNames, and the modifier can be passed as the key of the game status.
   * Example: { [store.gameStatus]: isBetsOpen } =>
   * Result: <baseClassName>_<store.gameStatus> will be added if the corresponding class is found in the styles module file and isBetsOpen is true.
   * Example #2: { [store.gameStatus]: true } => Result: <baseClassName>_<store.gameStatus> will always be added if found.
   * Example live: libraries/core/src/components/controls/toggle/Toggle.tsx
   */
  modifiers: Record<string, boolean | null | undefined>,
  options?: {
    /** Set to true if the result of strings should to be with baseClassName */
    withBaseClass?: boolean;
    /** @deprecated use modifiers: { white: true, green: true } */
    additionalModifiers?: string[];
    /** Option to pass classNames from props or Custom string which should be in result string of classNames.
     * Example: extraClassName: 'clear_fix' => Result: <baseClassName> <baseClassName>_<modifiers> clear_fix
     */
    extraClassName?: string;
  }
): string => {
  return useMemo(() => {
    let result = '';
    result += Object.keys(modifiers).reduce(
      (acc, modifier) => {
        if (!modifiers[modifier]) return acc;
        const className = styles[`${baseClassName}_${modifier}`];
        if (!className) return acc;
        return `${acc} ${className}`;
      },
      options?.withBaseClass ? (styles[baseClassName] ?? '') : ''
    );
    if (options?.additionalModifiers) {
      result += ' ';
      result += options.additionalModifiers?.reduce((acc, modifier) => {
        const className = styles[`${baseClassName}_${modifier}`];
        if (!className) return acc;
        return `${acc} ${className}`;
      }, '');
    }
    if (options?.extraClassName) {
      result += ' ';
      result += options.extraClassName;
    }
    return result.trim();
  }, [baseClassName, modifiers, options, styles]);
};
